/**
 * ErrorBoundary for the chat routes (/chat, /support).
 *
 * The chat pages are lazy-loaded chunks OUTSIDE the normal app shell — an
 * uncaught render error or a failed chunk download there renders a literally
 * BLANK screen (no shell, no fallback, nothing). That read as "the chat is
 * broken" (real reports). This boundary catches anything thrown under the
 * routes and shows a recoverable card: auto-reload once for chunk failures
 * (a deploy changed the hashes mid-session), manual reload otherwise.
 */
import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}
interface State {
  error: Error | null
  /** One automatic reload for chunk-load failures (stale deploy in a long-lived WebView). */
  autoRetried: boolean
}

export default class ChatErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, autoRetried: false }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  override componentDidCatch(error: Error) {
    // Chunk-load failures after a deploy are the common case — recover
    // automatically ONCE per pageload; anything else is shown as a card.
    const chunkFailure = /Loading chunk \d+ failed|error loading dynamically imported module|Importing a module script failed/i.test(
      String(error?.message ?? error),
    )
    const retried = (this.state.autoRetried || sessionStorage.getItem('chat_chunk_retried')) === '1' || this.state.autoRetried
    if (chunkFailure && !retried) {
      try { sessionStorage.setItem('chat_chunk_retried', '1') } catch { /* ignore */ }
      this.setState({ autoRetried: true })
      window.location.reload()
    }
  }

  override render() {
    if (!this.state.error) return this.props.children
    return (
      <div style={{ height: '100dvh', display: 'grid', placeItems: 'center', padding: 16 }}>
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Чат не смог открыться</h2>
          <p style={{ fontSize: 13.5, color: 'var(--color-ash)', lineHeight: 1.5, margin: '8px 0 16px' }}>
            Произошла ошибка при загрузке. Обновите страницу — обычно это помогает.
          </p>
          <button
            type="button"
            className="btn btn-acid"
            style={{ width: '100%', padding: '12px 16px', fontSize: 14.5 }}
            onClick={() => window.location.reload()}
          >
            Обновить
          </button>
        </div>
      </div>
    )
  }
}
